import React, { useState } from "react";
import {
  X,
  Cloud,
  HardDrive,
  ChevronDown,
  ChevronRight,
  Check,
  AlertTriangle,
  Clock,
  Plus,
  Minus,
  Edit,
  ArrowRight,
} from "lucide-react";
import { formatTimestampFull } from "../services/api";

/**
 * Conflict Resolution Modal
 * Shows differences between local and cloud data
 * Allows user to choose: all, by-section, or by-item
 */
const ConflictResolutionModal = ({
  isOpen,
  onClose,
  differences,
  onResolve,
  localData,
  cloudData,
}) => {
  const [resolutionMode, setResolutionMode] = useState("all"); // 'all', 'section', 'item'
  const [allChoice, setAllChoice] = useState(null); // 'local' | 'cloud'
  const [sectionChoices, setSectionChoices] = useState({
    settings: null,
    completedDays: null,
    tabs: null,
    subGoals: null,
    clipboardCards: null,
    quickLinkCards: null,
    rememberItems: null,
    conferenceCards: null,
  });
  const [itemChoices, setItemChoices] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    settings: true,
    completedDays: true,
    tabs: true,
    subGoals: true,
    clipboardCards: true,
    quickLinkCards: true,
    rememberItems: true,
    conferenceCards: true,
  });

  if (!isOpen || !differences) return null;

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleSectionChoice = (section, choice) => {
    setSectionChoices((prev) => ({
      ...prev,
      [section]: choice,
    }));
  };

  const handleItemChoice = (itemId, choice) => {
    setItemChoices((prev) => ({
      ...prev,
      [itemId]: choice,
    }));
  };

  const handleResolve = () => {
    let selections = {};
    let itemSelections = {};

    if (resolutionMode === "all") {
      // Apply the same choice to all sections
      selections = {
        settings: allChoice,
        completedDays: allChoice,
        tabs: allChoice,
        subGoals: allChoice,
        clipboardCards: allChoice,
        quickLinkCards: allChoice,
        rememberItems: allChoice,
        conferenceCards: allChoice,
      };
    } else if (resolutionMode === "section") {
      selections = sectionChoices;
    } else {
      // Item mode - use section choices as defaults, item choices as overrides
      selections = sectionChoices;
      itemSelections = itemChoices;
    }

    onResolve(selections, itemSelections);
  };

  const canResolve = () => {
    if (resolutionMode === "all") {
      return allChoice !== null;
    }
    if (resolutionMode === "section") {
      // Check all sections with differences have choices
      const sectionsWithDiffs = Object.entries(differences.categories)
        .filter(([, data]) => data.hasDiff)
        .map(([key]) => key);
      return sectionsWithDiffs.every((key) => sectionChoices[key] !== null);
    }
    // For item mode, require at least section-level choices
    return Object.values(sectionChoices).some((v) => v !== null);
  };

  // Render a single difference item
  const renderDiffItem = (type, local, cloud, itemId = null) => {
    const showItemChoice = resolutionMode === "item" && itemId;
    const itemChoice = itemChoices[itemId];

    return (
      <div className="flex items-start gap-3 text-sm py-2 border-b border-gray-700/50 last:border-0">
        {/* Local value */}
        <div
          className={`flex-1 p-2 rounded-lg ${
            itemChoice === "local"
              ? "bg-green-500/20 border border-green-500/50"
              : "bg-gray-700/50"
          } ${showItemChoice ? "cursor-pointer hover:bg-green-500/10" : ""}`}
          onClick={() => showItemChoice && handleItemChoice(itemId, "local")}
        >
          <div className="flex items-center gap-1 text-xs text-gray-400 mb-1">
            <HardDrive className="w-3 h-3" />
            Local
          </div>
          <div className="text-cyan-200 break-words whitespace-pre-wrap">
            {formatFieldValue(local)}
          </div>
        </div>

        <ArrowRight className="w-4 h-4 text-gray-500 flex-shrink-0 mt-4" />

        {/* Cloud value */}
        <div
          className={`flex-1 p-2 rounded-lg ${
            itemChoice === "cloud"
              ? "bg-blue-500/20 border border-blue-500/50"
              : "bg-gray-700/50"
          } ${showItemChoice ? "cursor-pointer hover:bg-blue-500/10" : ""}`}
          onClick={() => showItemChoice && handleItemChoice(itemId, "cloud")}
        >
          <div className="flex items-center gap-1 text-xs text-gray-400 mb-1">
            <Cloud className="w-3 h-3" />
            Cloud
          </div>
          <div className="text-cyan-200 break-words whitespace-pre-wrap">
            {formatFieldValue(cloud)}
          </div>
        </div>
      </div>
    );
  };

  // Render settings differences
  const renderSettingsDiff = () => {
    const { changes } = differences.categories.settings;
    if (!changes || changes.length === 0) return null;

    return changes.map((change, idx) => (
      <div key={idx} className="mb-2">
        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
          {change.key}
        </div>
        {renderDiffItem("setting", change.local, change.cloud, `settings-${change.key}`)}
      </div>
    ));
  };

  // Render completed days differences
  const renderCompletedDaysDiff = () => {
    const { added, removed } = differences.categories.completedDays;

    return (
      <div className="space-y-2">
        {added?.length > 0 && (
          <div>
            <div className="flex items-center gap-1 text-xs text-green-400 mb-1">
              <Plus className="w-3 h-3" />
              Added locally ({added.length})
            </div>
            <div className="flex flex-wrap gap-1">
              {added.map((day) => (
                <span key={day} className="px-2 py-1 bg-green-500/20 rounded text-xs text-green-300">
                  {day}
                </span>
              ))}
            </div>
          </div>
        )}
        {removed?.length > 0 && (
          <div>
            <div className="flex items-center gap-1 text-xs text-red-400 mb-1">
              <Minus className="w-3 h-3" />
              In cloud only ({removed.length})
            </div>
            <div className="flex flex-wrap gap-1">
              {removed.map((day) => (
                <span key={day} className="px-2 py-1 bg-red-500/20 rounded text-xs text-red-300">
                  {day}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Fields that aren't meaningful to show as a "difference" to the user.
  const IGNORED_DIFF_KEYS = new Set(["id", "createdAt", "updatedAt", "order"]);

  const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Turn a nested object into one readable line, picking the shape that fits the
  // kinds of objects this app stores (recurrence, links, blocks, conference
  // entries) and falling back to a label or key:value list. Never dumps JSON.
  const summarizeObject = (obj) => {
    if (!obj || typeof obj !== "object") return String(obj ?? "—");

    // Recurrence: { weekdays: number[], endDate }
    if (Array.isArray(obj.weekdays) && obj.endDate) {
      const days = obj.weekdays.map((d) => WEEKDAY_LABELS[d] ?? d).join(", ");
      return `Weekly on ${days || "—"} until ${obj.endDate}`;
    }
    // Quick link: { url, description, completed }
    if ("url" in obj) {
      const desc = obj.description ? `${obj.description} — ` : "";
      return `${desc}${obj.url || "(no url)"}${obj.completed ? " ✓" : ""}`;
    }
    // Clipboard block: { type, text, checked }
    if ("type" in obj && "text" in obj) {
      const box = obj.type === "todo" ? (obj.checked ? "☑ " : "☐ ") : "";
      return `${box}${obj.text || ""}`.trim() || "(empty line)";
    }
    // Conference entry: { year, venue, submissionDeadline, outcome, ... }
    if ("year" in obj || "venue" in obj || "submissionDeadline" in obj) {
      const bits = [];
      if (obj.year) bits.push(String(obj.year));
      if (obj.venue) bits.push(obj.venue);
      if (obj.submissionDeadline) bits.push(`deadline ${obj.submissionDeadline}`);
      if (obj.outcome && obj.outcome !== "pending") bits.push(obj.outcome);
      return bits.join(" · ") || "(entry)";
    }
    // Generic: prefer a human label, else a compact key:value list.
    const label = obj.title || obj.text || obj.name;
    if (label) return String(label);
    return (
      Object.entries(obj)
        .filter(([k]) => k !== "id")
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ") || "(empty)"
    );
  };

  // Human-readable rendering of a single field value for the diff view. Returns
  // a string; multi-item values use newlines (rendered with whitespace-pre-wrap).
  const formatFieldValue = (val) => {
    if (val === null || val === undefined || val === "") return "—";
    if (typeof val === "boolean") return val ? "yes" : "no";
    if (Array.isArray(val)) {
      if (val.length === 0) return "(empty)";
      if (typeof val[0] === "object" && val[0] !== null) {
        return val.map((v) => `• ${summarizeObject(v)}`).join("\n");
      }
      return val.join(", ");
    }
    if (typeof val === "object") return summarizeObject(val);
    return String(val);
  };

  // Compute which fields actually differ between a local and cloud version of
  // the same item, so the modal can show "deadline: X → Y" instead of just the
  // unchanged title.
  const getFieldDiffs = (local, cloud) => {
    const diffs = [];
    const keys = new Set([
      ...Object.keys(local || {}),
      ...Object.keys(cloud || {}),
    ]);
    for (const key of keys) {
      if (IGNORED_DIFF_KEYS.has(key)) continue;
      if (JSON.stringify(local?.[key]) !== JSON.stringify(cloud?.[key])) {
        diffs.push({ key, local: local?.[key], cloud: cloud?.[key] });
      }
    }
    return diffs;
  };

  // Render array-based differences (tabs, goals, clipboard, etc.)
  const renderArrayDiff = (category, displayField = "name") => {
    const { changes } = differences.categories[category];
    if (!changes) return null;

    return (
      <div className="space-y-3">
        {changes.added?.length > 0 && (
          <div>
            <div className="flex items-center gap-1 text-xs text-green-400 mb-2">
              <Plus className="w-3 h-3" />
              Added locally ({changes.added.length})
            </div>
            {changes.added.map((item) => (
              <div
                key={item.id}
                className={`p-2 rounded-lg mb-1 ${
                  resolutionMode === "item" ? "cursor-pointer" : ""
                } ${
                  itemChoices[item.id] === "local"
                    ? "bg-green-500/20 border border-green-500/50"
                    : "bg-gray-700/50"
                }`}
                onClick={() => resolutionMode === "item" && handleItemChoice(item.id, "local")}
              >
                <div className="text-cyan-200 text-sm">
                  {item[displayField] || item.text || item.title || summarizeObject(item)}
                </div>
              </div>
            ))}
          </div>
        )}

        {changes.removed?.length > 0 && (
          <div>
            <div className="flex items-center gap-1 text-xs text-red-400 mb-2">
              <Minus className="w-3 h-3" />
              In cloud only ({changes.removed.length})
            </div>
            {changes.removed.map((item) => (
              <div
                key={item.id}
                className={`p-2 rounded-lg mb-1 ${
                  resolutionMode === "item" ? "cursor-pointer" : ""
                } ${
                  itemChoices[item.id] === "cloud"
                    ? "bg-blue-500/20 border border-blue-500/50"
                    : "bg-gray-700/50"
                }`}
                onClick={() => resolutionMode === "item" && handleItemChoice(item.id, "cloud")}
              >
                <div className="text-cyan-200 text-sm">
                  {item[displayField] || item.text || item.title || summarizeObject(item)}
                </div>
              </div>
            ))}
          </div>
        )}

        {changes.modified?.length > 0 && (
          <div>
            <div className="flex items-center gap-1 text-xs text-yellow-400 mb-2">
              <Edit className="w-3 h-3" />
              Modified ({changes.modified.length})
            </div>
            {changes.modified.map(({ local, cloud }) => {
              const fieldDiffs = getFieldDiffs(local, cloud);
              const label =
                local[displayField] || local.text || local.title || `#${local.id}`;
              const sel = itemChoices[local.id];
              return (
                <div
                  key={local.id}
                  className="mb-3 rounded-xl border border-gray-700 overflow-hidden"
                >
                  <div className="px-3 py-2 bg-gray-800/70 flex items-center justify-between gap-2">
                    <span className="text-cyan-300 text-sm font-medium truncate">
                      {label}
                    </span>
                    {resolutionMode === "item" && (
                      <span className="flex gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleItemChoice(local.id, "local")}
                          className={`px-2 py-0.5 rounded text-[11px] font-medium ${
                            sel === "local"
                              ? "bg-green-500 text-white"
                              : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                          }`}
                        >
                          Local
                        </button>
                        <button
                          onClick={() => handleItemChoice(local.id, "cloud")}
                          className={`px-2 py-0.5 rounded text-[11px] font-medium ${
                            sel === "cloud"
                              ? "bg-blue-500 text-white"
                              : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                          }`}
                        >
                          Cloud
                        </button>
                      </span>
                    )}
                  </div>
                  <div className="p-2 space-y-2">
                    {fieldDiffs.length === 0 ? (
                      <div className="text-xs text-gray-500 px-1">
                        Reordered or metadata-only change.
                      </div>
                    ) : (
                      fieldDiffs.map((fd) => (
                        <div key={fd.key}>
                          <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1 px-1">
                            {fd.key}
                          </div>
                          {renderDiffItem(category, fd.local, fd.cloud)}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Section component
  const Section = ({ title, sectionKey, children, icon: Icon }) => {
    const hasDiff = differences.categories[sectionKey]?.hasDiff;
    if (!hasDiff) return null;

    const isExpanded = expandedSections[sectionKey];
    const choice = sectionChoices[sectionKey];

    return (
      <div className="border border-gray-700 rounded-xl overflow-hidden mb-3">
        {/* Section header */}
        <div
          className="flex items-center justify-between p-3 bg-gray-800 cursor-pointer hover:bg-gray-750"
          onClick={() => toggleSection(sectionKey)}
        >
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
            {Icon && <Icon className="w-4 h-4 text-cyan-400" />}
            <span className="text-white font-medium">{title}</span>
          </div>

          {/* Section-level choice buttons */}
          {(resolutionMode === "section" || resolutionMode === "item") && (
            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
              <button
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                  choice === "local"
                    ? "bg-green-500 text-white"
                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                }`}
                onClick={() => handleSectionChoice(sectionKey, "local")}
              >
                <HardDrive className="w-3 h-3 inline mr-1" />
                Local
              </button>
              <button
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                  choice === "cloud"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                }`}
                onClick={() => handleSectionChoice(sectionKey, "cloud")}
              >
                <Cloud className="w-3 h-3 inline mr-1" />
                Cloud
              </button>
            </div>
          )}
        </div>

        {/* Section content */}
        {isExpanded && <div className="p-3 bg-gray-800/50">{children}</div>}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-gray-900 rounded-3xl border border-gray-700 shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-yellow-500" />
              <h2 className="text-xl font-bold text-white">Sync Conflict Detected</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-800 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Timestamps */}
          <div className="flex items-center justify-between text-sm bg-gray-800/50 rounded-xl p-3">
            <div className="flex items-center gap-2">
              <HardDrive className="w-4 h-4 text-green-400" />
              <span className="text-gray-400">Local:</span>
              <span className="text-green-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatTimestampFull(differences.localTimestamp)}
              </span>
              {differences.localNewer && (
                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">
                  Newer
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Cloud className="w-4 h-4 text-blue-400" />
              <span className="text-gray-400">Cloud:</span>
              <span className="text-blue-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatTimestampFull(differences.cloudTimestamp)}
              </span>
              {differences.cloudNewer && (
                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">
                  Newer
                </span>
              )}
            </div>
          </div>

          {/* Resolution mode selector */}
          <div className="mt-4 flex items-center gap-2">
            <span className="text-gray-400 text-sm">Resolution mode:</span>
            <div className="flex gap-1 bg-gray-800 rounded-lg p-1">
              <button
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  resolutionMode === "all"
                    ? "bg-cyan-500 text-white"
                    : "text-gray-400 hover:text-white"
                }`}
                onClick={() => setResolutionMode("all")}
              >
                All at once
              </button>
              <button
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  resolutionMode === "section"
                    ? "bg-cyan-500 text-white"
                    : "text-gray-400 hover:text-white"
                }`}
                onClick={() => setResolutionMode("section")}
              >
                By section
              </button>
              <button
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  resolutionMode === "item"
                    ? "bg-cyan-500 text-white"
                    : "text-gray-400 hover:text-white"
                }`}
                onClick={() => setResolutionMode("item")}
              >
                By item
              </button>
            </div>
          </div>

          {/* All-at-once choice */}
          {resolutionMode === "all" && (
            <div className="mt-4 flex items-center gap-4">
              <span className="text-gray-400 text-sm">Keep:</span>
              <button
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all ${
                  allChoice === "local"
                    ? "bg-green-500 text-white"
                    : "bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700"
                }`}
                onClick={() => setAllChoice("local")}
              >
                <HardDrive className="w-4 h-4" />
                All Local Data
                {differences.localNewer && (
                  <span className="text-xs opacity-75">(Newer)</span>
                )}
              </button>
              <button
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all ${
                  allChoice === "cloud"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700"
                }`}
                onClick={() => setAllChoice("cloud")}
              >
                <Cloud className="w-4 h-4" />
                All Cloud Data
                {differences.cloudNewer && (
                  <span className="text-xs opacity-75">(Newer)</span>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Body - scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-3">
            Differences Found
          </div>

          {/* Settings */}
          <Section title="Settings" sectionKey="settings">
            {renderSettingsDiff()}
          </Section>

          {/* Completed Days */}
          <Section title="Completed Days" sectionKey="completedDays">
            {renderCompletedDaysDiff()}
          </Section>

          {/* Tabs & Goals */}
          <Section title="Tabs & Goals" sectionKey="tabs">
            {renderArrayDiff("tabs", "name")}
          </Section>

          {/* Sub-Goals */}
          <Section title="Sub-Goals" sectionKey="subGoals">
            {renderArrayDiff("subGoals", "text")}
          </Section>

          {/* Clipboard Cards */}
          <Section title="Clipboard Cards" sectionKey="clipboardCards">
            {renderArrayDiff("clipboardCards", "title")}
          </Section>

          {/* Quick Links */}
          <Section title="Quick Links" sectionKey="quickLinkCards">
            {renderArrayDiff("quickLinkCards", "title")}
          </Section>

          {/* Remember Items */}
          <Section title="Remember" sectionKey="rememberItems">
            {renderArrayDiff("rememberItems", "text")}
          </Section>

          {/* Conferences */}
          <Section title="Conferences" sectionKey="conferenceCards">
            {renderArrayDiff("conferenceCards", "title")}
          </Section>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-gray-800 text-gray-300 rounded-xl hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleResolve}
              disabled={!canResolve()}
              className={`px-6 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
                canResolve()
                  ? "bg-cyan-500 text-white hover:bg-cyan-600"
                  : "bg-gray-700 text-gray-500 cursor-not-allowed"
              }`}
            >
              <Check className="w-4 h-4" />
              Apply Resolution
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConflictResolutionModal;

